
import Vue from 'vue';
import { Data as ArticleData, Headlines } from '@root/modules/article/types/article';
import { Data as HeadlineData } from '@root/modules/headlines/types/headlines';
import ArticleCxenseService from '@root/modules/article/services/ArticleCxense.service';
import UniversalHeadlinesService from '@root/modules/headlines/services/UniversalHeadlines.service';
import MostReadArticlesService from '@root/modules/headlines/services/MostReadArticles.service';
import AdsLayout from '@root/common/components/layout/AdsLayout.vue';
import ArticleItem from '@root/modules/article/components/Article.vue';
import ArticleLiveBlogHeader from '@root/modules/article/components/ArticleLiveBlogHeader.vue';
import getChannelDomain from '@root/modules/channel/utils/getChannelDomain';
import CategoryHeader from '@root/modules/article/components/multimedija/CategoryHeader.vue';
import Sidebar from '@root/modules/article/components/multimedija/Sidebar.vue';
import isTildaArticle from '@root/modules/article/utils/isTildaArticle';
import isFullWidthArticle from '@root/modules/article/utils/isFullWidthArticle';
import ReportMistake from '@root/modules/article/components/ReportMistake.vue';
import isMobileView from '@root/common/utils/isMobileView';

interface Data {
  loadingFailed: boolean;
  articles: string[];
  nextArticleLoading: boolean;
  previewMode: boolean;
  isMobileView: boolean;
  reportMistake: {
    isCollapsed: boolean;
    text: string;
    top: number;
    left: number;
    articleId: number | null;
    articleTitle: string | null;
  };
}

interface Computed {
  crArticles: string[];
  activeArticle: ArticleData;
  isVideoArticle: boolean;
  isMultimedijaArticle: boolean;
  isTildaArticle: boolean;
  isFullWidthArticle: boolean;
  isLiveBlogArticle: boolean;
  disabledAdsList: string[];
  hasTickarooLiveblog: boolean;
}

interface Methods {
  loadNextArticle: () => void;
  waitForNextArticle: (loaded: boolean) => void;
  fetchNextArticles: () => void;
  getPtArticles: (blockId: string) => Promise<number[]>;
  getSameCategoryArticles: () => Promise<number[]>;
  getMostReadArticles: () => Promise<number[]>;
  getArticleHeadlines: (relatedHeadlineIds: number[], limit?: number) => Promise<Headlines>;
  resetArticles: (articles: string[]) => void;
  showReportMistakeIcon: (event: any) => void;
}

export default Vue.extend<Data, Methods, Computed, unknown>({
  components: {
    AdsLayout,
    ArticleItem,
    ArticleLiveBlogHeader,
    CategoryHeader,
    Sidebar,
    ReportMistake,
  },
  validate({ params }) {
    return /^\d+$/.test(params.id) && /^[a-z0-9-]+$/.test(params.slug);
  },
  data() {
    return {
      loadingFailed: false,
      articles: [this.$route.params.id],
      nextArticleLoading: false,
      previewMode: !!this.$route.query.preview,
      isMobileView: false,
      reportMistake: {
        isCollapsed: true,
        text: '',
        top: 0,
        left: 0,
        articleId: null,
        articleTitle: null,
      },
    };
  },
  computed: {
    crArticles() {
      return this.$store.getters['article/getCrArticles'];
    },
    activeArticle() {
      return this.$store.getters['article/getActiveArticle'];
    },
    isVideoArticle() {
      const { id } = this.$channelConfig('settings');

      return id === 'tv';
    },
    isMultimedijaArticle() {
      const { id } = this.$channelConfig('settings');
      return id === 'multimedija';
    },
    isTildaArticle() {
      const customCategorySettings = this.$channelConfig('page').category?.component?.header;
      return isTildaArticle(this.$route, customCategorySettings);
    },
    isFullWidthArticle() {
      return this.activeArticle && isFullWidthArticle(this.activeArticle);
    },
    isLiveBlogArticle() {
      return this.activeArticle && this.activeArticle.settings?.articleTemplate && this.activeArticle.settings?.articleTemplate === 'liveBlog';
    },
    disabledAdsList() {
      return this.$store.getters['adForm/getDisabledAdsList'];
    },
    hasTickarooLiveblog() {
      return this.activeArticle?.embedTypes?.includes('tickaroo') || false;
    },
  },
  watch: {
    activeArticle: {
      async handler(article) {
        const id = article && String(article.id);

        if (!id) {
          return;
        }

        if (this.isTildaArticle) {
          return;
        }

        const isFirstArticle = this.articles.indexOf(id) === 0;

        if (isFirstArticle) {
          if (!this.previewMode) {
            this.fetchNextArticles();
          }

          // Get cxense articles
          const widgetId = this.$channelConfig('page').article.component.cXense?.widgetId;
          if (widgetId && !this.$store.getters['article/getArticleCxenseArticles']) {
            const articleCxenseService = new ArticleCxenseService();
            const cxenseArticles = await articleCxenseService.fetch({ widgetId });
            if (cxenseArticles.length) {
              this.$store.commit('article/setArticleCxenseArticles', cxenseArticles);
              this.$store.commit('article/setArticleCxenseHeadlines', await this.getArticleHeadlines(cxenseArticles.map((article) => Number(article.id))));
            }
          }
        }
      },
      immediate: true,
    },
  },
  mounted() {
    this.isMobileView = isMobileView();
  },
  destroyed() {
    this.$store.commit('article/setActiveArticlePath', null);
    this.$store.commit('article/setCrArticles', []);
    window.removeEventListener('scroll', this.loadNextArticle);
  },
  methods: {
    loadNextArticle() {
      const d = document.documentElement;
      const w = document.querySelector('.articles-wrapper') as HTMLElement;
      if (w) {
        const { nextArticleOffset = d.clientHeight + 900 } = this.$channelConfig('page').article.component.article;

        const userHasScrolled = window.scrollY > 200;
        const offset = window.scrollY + nextArticleOffset;
        const height = w.offsetHeight;

        // Load next article
        if (userHasScrolled && offset >= height && !this.nextArticleLoading) {
          const nextArticle = this.crArticles[this.articles.length - 1];
          if (nextArticle) {
            this.nextArticleLoading = true;
            this.articles.push(nextArticle);
            this.$store.commit('article/setComposerTarget', nextArticle);
          }
        }
      }
    },
    async fetchNextArticles() {
      const articleId = this.$route.params.id;

      if (this.isFullWidthArticle) {
        return;
      }

      const loadNextArticle = () => {
        this.loadNextArticle();
        window.addEventListener('scroll', this.loadNextArticle);
      };

      if (this.crArticles.length !== 0) {
        loadNextArticle();
        return;
      }

      // Get nextArticlesIds from same category
      let nextArticlesIds = await this.getSameCategoryArticles();

      // If category is fresh (less then 2 articles) as fallback fetch most read articles
      if (nextArticlesIds.length < 2 && !this.isMultimedijaArticle) {
        nextArticlesIds = await this.getMostReadArticles();
      }

      // Avoid first article to be duplicated in continuous reading
      let filteredArticlesIds = nextArticlesIds.filter((id) => String(id) !== articleId);

      // Inject PT articles into CR articles list (position 1, 2) if crArticlesBlockId specified and block has at least 2 articles
      let ptArticlesIds = [];

      // Look for category level specified crArticlesBlockId value
      const categorySlug = this.activeArticle?.primaryCategory.slug as string; // FIX ME!!! Slug should be always defined.
      const categoryArticleSettings = this.$channelConfig('page').category.component?.article;
      const categoryCrArticlesBlockId = categoryArticleSettings?.find((c) => c.categories.includes(categorySlug))?.crArticlesBlockId;

      // Look for channel level specified crArticlesBlockId value
      const { crArticlesBlockId } = this.$channelConfig('page').article.component.article;

      // Fetch and inject PT articles if crArticlesBlockId value is found
      if (categoryCrArticlesBlockId || crArticlesBlockId) {
        const blockId = (categoryCrArticlesBlockId || crArticlesBlockId) as string;
        ptArticlesIds = await this.getPtArticles(blockId);

        if (ptArticlesIds.length > 1) {
          filteredArticlesIds = [...ptArticlesIds, ...filteredArticlesIds.slice(0, 8)];
        }
      }

      this.$store.commit('article/setCrArticles', filteredArticlesIds);

      if (filteredArticlesIds.length > 0) {
        loadNextArticle();
      }
    },
    async getSameCategoryArticles() {
      const { id } = this.$channelConfig('settings');
      const universalHeadlinesService = new UniversalHeadlinesService();
      const categoryId = this.activeArticle.primaryCategory.id;
      const articlesLimit = this.isMultimedijaArticle ? 50 : 10;
      const variables: any = { categoryId, excludeChannels: [], getCount: this.isMultimedijaArticle, limit: articlesLimit, lite: true };

      // Exclude video channel articles if current channel is not video
      if (id !== 'tv') {
        variables.excludeChannels.push('www.delfi.lt/tv');
      }

      // Exclude multimedija channel articles if current channel is not multimedija
      if (id !== 'multimedija') {
        variables.excludeChannels.push('www.delfi.lt/multimedija');
      }

      const headlines = await universalHeadlinesService.fetch(variables);

      // Store multimedija articles list for re-use in components
      if (this.isMultimedijaArticle && headlines.items.length) {
        this.$store.commit('article/setMultimedijaArticles', headlines);
      }

      return headlines.items.map((item) => item.id);
    },
    async getPtArticles(blockId) {
      const universalHeadlinesService = new UniversalHeadlinesService();
      const variables: any = { blockId, limit: 2, lite: true };
      const headlines = await universalHeadlinesService.fetch(variables);

      return headlines.items.map((item) => item.id);
    },
    async getMostReadArticles() {
      const { id } = this.$channelConfig('settings');
      const domain = getChannelDomain(id) || 'www.delfi.lt';
      const mostReadArticlesService = new MostReadArticlesService();
      const headlines = await mostReadArticlesService.fetch({ viewsSince: 'SINCE_24_HOURS', limit: 10, domain, lite: true });
      return headlines.items.map((item: HeadlineData) => item.id);
    },
    async getArticleHeadlines(headlineIds, limit = 0) {
      const universalHeadlinesService = new UniversalHeadlinesService();
      const headlines = await universalHeadlinesService.fetch({ id: headlineIds, limit, lite: true });
      return headlines.items;
    },
    waitForNextArticle(loaded) {
      // If more than 1 article fails to load in a row
      // then don't try to load new articles
      if (!loaded && this.loadingFailed) {
        return;
      }

      this.nextArticleLoading = false;
      this.loadingFailed = !loaded;
    },
    resetArticles(articles) {
      this.articles = articles;
    },
    showReportMistakeIcon(event) {
      const allowedSelectionTags = ['H1', 'P'];

      if (allowedSelectionTags.includes(event.target.nodeName)) {
        const selection = window.getSelection();
        const selectionArea = this.$refs.articlesWrapper as HTMLDivElement;

        if (selectionArea) {
          this.reportMistake.isCollapsed = !!selection?.isCollapsed;

          if (selection && selection.type === 'Range' && selectionArea.contains(selection.anchorNode)) {
            const range = selection.getRangeAt(0);

            const container = range.commonAncestorContainer;
            const element = container.nodeType === Node.ELEMENT_NODE ? (container as Element) : container.parentElement;

            if (element) {
              const articleElement = element.closest('.article') as HTMLElement | null;

              if (articleElement) {
                const articleElementId = articleElement.id;
                const articleId = Number(articleElementId.replace(/^article-/, ''));
                this.reportMistake.articleId = articleId;

                const titleElement = articleElement.querySelector('.article-info__title') as HTMLElement | null;
                if (titleElement) {
                  const titleText = titleElement.textContent?.trim() ?? '';
                  this.reportMistake.articleTitle = titleText;
                }
              }
            }

            const rect = range.getBoundingClientRect();
            const selectionAreaRect = selectionArea.getBoundingClientRect();

            this.reportMistake.top = rect.top - selectionAreaRect.top - 40;
            this.reportMistake.left = rect.left - selectionAreaRect.left + rect.width / 2;
            this.reportMistake.text = selection.toString();
          }
        }
      }
    },
  },
});
